import get from 'lodash/get';
import { NavbarOptionFeatureFlag } from 'modules/nav/types/navigation';
import { hasFeatureFlagEnabled } from 'modules/nav/utils/hasFeatureFlagEnabled';
import { RouterState } from 'redux-first-history';
import { createSelector } from 'reselect';
import { sha256 as Sha256 } from 'sha.js';
import { ORG_PERMISSIONS, doesRbacPermissionHaveAccess } from 'utils/permissions';
import { getUrlParams } from 'utils/urlParams';

import { ProjectPermission } from 'modules/domains/project/permissions';
import { USER_ACCOUNT_TYPES } from './constants';
import { UserContext, UserContextProject, UserContextRootState } from './types';

/**
 * Selector for the user context state.
 */
export const userContextSelector = (state: UserContextRootState) => state.userContext || {};

/**
 * Creates a selector for the user context state of a given attribute.
 */
export const createUserContextSelector = <AttrKeyType extends keyof UserContext>(
  /** The attribute of the state to create a selector of. */
  attr: AttrKeyType,
) => createSelector(userContextSelector, (userContext) => userContext[attr]);

export const getRouterLocation = (state: { router: RouterState }) => state.router.location;

/**
 * Creates a selector for the user context state of a given attribute.
 */
export const createUserContextSelectorWithDefaultValue = <AttrKeyType extends keyof UserContext>(
  /** The attribute of the state to create a selector of. */
  attr: AttrKeyType,
  /** Value that is selected when the attribute is not set in the Redux store. */
  defaultValue: Exclude<UserContext[AttrKeyType], null | undefined>,
) =>
  createSelector(
    userContextSelector,
    (userContext) =>
      (userContext[attr] || defaultValue) as Exclude<UserContext[AttrKeyType], null | undefined>,
  );

/**
 * Selectors for the user context state.
 */
export const selectUserContext = userContextSelector;
export const selectIsUserContextLoaded = createUserContextSelector('isLoaded');
export const selectOrganizationId = createUserContextSelector('orgId');
export const selectUserAvatarUrl = createUserContextSelector('avatarUrl');
export const selectFirstName = createUserContextSelectorWithDefaultValue('firstName', ' ');
export const selectLastName = createUserContextSelectorWithDefaultValue('lastName', ' ');
export const selectUserId = createUserContextSelectorWithDefaultValue('userId', ' ');
export const selectEmail = createUserContextSelector('email');
export const selectCompanyName = createUserContextSelector('companyName');
export const selectActingUser = createUserContextSelector('actingUser');
export const selectIsBossOrSalesBossLevel = createUserContextSelector('isBossOrSalesBossLevel');
export const selectIsOrgAdmin = createUserContextSelector('orgAdmin');
export const selectUserProject = createUserContextSelector('project');
export const selectProjects = createUserContextSelectorWithDefaultValue('projects', []);
export const selectIsGdprAccepted = createUserContextSelector('isGdprAccepted');
export const selectRole = createUserContextSelector('permissions');
export const getFeatureFlags = createUserContextSelectorWithDefaultValue('featureFlags', {});

export const selectAllowEmailBasedProjectCreation = createUserContextSelector(
  'allowEmailBasedProjectCreation',
);

export const selectIsShowLiveChatFlagOn = createUserContextSelector('showLiveChat');

export const getCanManageMessagingServiceProviders = createUserContextSelector(
  'canManageMessagingServiceProviders',
);

export const selectUserAccountType = createUserContextSelector('accountType');
export const selectUserHasIterableEmail = createSelector(
  userContextSelector,
  ({ email = '' }) => !!email?.includes('@iterable.com'),
);

/**
 * Create a selector for the user project of a user context
 */
export const createUserProjectSelector = <AttrKeyType extends keyof UserContextProject>(
  attr: AttrKeyType,
) => createSelector(selectUserProject, (userProject) => (userProject && userProject[attr]) || '');
export const selectUserProjectDefaultSenderEmail = createUserProjectSelector('defaultSenderEmail');
export const selectUserProjectName = createUserProjectSelector('name');
export const selectUserProjectId = createUserProjectSelector('id');
export const selectUserProjectToken = createUserProjectSelector('token');
export const selectUserProjectIsUuidBased = createUserProjectSelector('isUuidBased');
export const selectUserProjectUuidProjectType = createSelector(
  selectUserProject,
  (userProject) => userProject && userProject?.uuidProjectType,
);
export const selectUserProjectCustomEventManageByApiKey = createUserProjectSelector(
  'customEventManageByApiKey',
);
export const selectUserProjectTimeZone = createUserProjectSelector('timeZone');
export const selectUserRbacPermissions = createUserContextSelectorWithDefaultValue(
  'rbacPermissions',
  [],
);

export const selectUrlSearchParamString = (state: { router: RouterState }) =>
  state.router?.location?.search || '';
export const selectUserBetaToggles = createUserContextSelectorWithDefaultValue(
  'userBetaToggles',
  {},
);

export const selectUrlParameterFeatureFlags = createSelector(
  [selectUrlSearchParamString, selectUserAccountType],
  (urlSearchParamString, accountType) => {
    if (accountType !== USER_ACCOUNT_TYPES.Employee || urlSearchParamString.length < 1) {
      return {};
    }
    const parameterObject = getUrlParams(urlSearchParamString);
    return Object.keys(parameterObject).reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = parameterObject[key].toLowerCase() === 'true';
      return acc;
    }, {});
  },
);

/*
  This merges the feature flags returned from the backend with any overrides
  specified in the URL. If the URL and feature flag backend call have different
  values, the URL value set will take precedence. The URL values only apply if the
  user is "Baus".
*/
export const selectFeatureFlags = createSelector(
  [getFeatureFlags, selectUrlParameterFeatureFlags],
  (featureFlags, urlParameterOverrides) => ({
    ...featureFlags,
    ...urlParameterOverrides,
  }),
);

export const selectUserOrgAdmin = createUserContextSelectorWithDefaultValue('orgAdmin', false);

export const selectIsSendTimeOptimizationInsightsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SendTimeOptimizationInsights,
);

export const selectEtlIntegrationEnabled = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.EtlIntegration,
);

export const selectNextBestActionEnabled = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.NextBestAction,
);

export const selectNBAJourneysEnabled = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.NBAJourneys,
);

export const selectCopyAssistEnabled = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CopyAssist,
);

export const selectCampaignReactificationV1Enabled = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CampaignReactificationV1,
);

export const selectLocaleImprovementsEnabled = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.LocaleImprovements,
);

export const selectIsIterableGoalsFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.IterableGoals,
);

export const selectIsIterableGoalsStretchFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.IterableGoalsStretch,
);

export const selectIsCatalogCollectionsFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CatalogQueries,
);

export const selectIsCatalogShowSizeFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CatalogShowSize,
);

export const selectIsCatalogTriggerValuesOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CatalogTriggerValues,
);

export const selectIsProductRecommendationsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ProductRecommendations,
);

export const selectIsAmpForEmailFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.AmpForEmail,
);

export const selectIsExportReportsAsCsvFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ExportReportsAsCsv,
);

export const selectIsCampaignAndTemplateEditorsFolderBreadcrumbFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CampaignAndTemplateEditorsFolderBreadcrumb,
);

export const selectIsDataFeedRateLimitingFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.DataFeedRateLimiting,
);

export const selectIsMetricDefinitionsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.MetricDefinitions,
);

export const selectIsExportUrlFiltersCAFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ExportUrlFiltersCA,
);

export const selectIsSMSClickWebhookUIFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SMSClickWebhookUI,
);

export const selectIsSMSLinkShorteningFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SMSLinkShortening,
);

export const selectIsSMSContactCardUIOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SMSTemplateContactCardUI,
);

export const selectIsSendMmsOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SendMms,
);

export const selectIsEmbeddedMessagingCoreFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.EmbeddedMessagingCore,
);

export const selectIsEmbeddedMessagingJourneysFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.EmbeddedMessagingJourneys,
);

export const selectIsJourneyGoalsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.JourneyGoals,
);

export const selectIsJourneySettingsRefactorFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.JourneySettingsRefactor,
);

export const selectIsJourneyGoalsWithExitsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.JourneyGoalsWithExits,
);

export const selectIsJourneyTestingFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.JourneyTesting,
);

export const selectIsGlobalSmsFirstPartyUIFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.GlobalSmsFirstPartyUI,
);

export const selectIsIOSFirebaseUIFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.IOSFirebaseUI,
);

export const selectIsPredictiveGoalsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.PredictiveGoals,
);

export const selectIsInAppRecallUIFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.InAppRecallUI,
);

export const selectIsWebInAppReportingFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.WebInAppReporting,
);

export const selectIsContextualizedMetricsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ContextualizedMetrics,
);

export const selectIsMIApplyButtonFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.MIApplyButton,
);

export const selectIsBrandAffinityFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.BrandAffinity,
);

export const selectIsContactLookupFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ContactLookup,
);

export const selectIsUserProfilePageLookupByUserIdFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.UserProfilePageLookupByUserId,
);

export const selectIsCampaignPerformanceAlertsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CampaignPerformanceAlerts,
);

export const selectIsJourneysCampaignPerformanceAlertsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.JourneysCampaignPerformanceAlerts,
);

export const selectIsCampaignAlertOverridesFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CampaignAlertOverrides,
);

export const selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CampaignPerformanceAlertsHistory,
);

export const selectIsQuietHoursFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.QuietHours,
);

export const selectIsQuietHoursAbortCampaignFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.QuietHoursAbortCampaign,
);

export const selectIsSupportChatTestFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SupportChatTest,
);

export const selectIsSmsVerificationFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SmsVerification,
);

export const selectIsRokuTemplatingFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.RokuTemplating,
);

export const selectIsDynamicContentReportingSnippetsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.DynamicContentReportingSnippets,
);

export const selectIsAnalyticsDynamicHeatmapsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.AnalyticsDynamicHeatmaps,
);

export const selectIsSegmentCardinalityImprovementsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SegmentCardinalityImprovements,
);

export const selectIsTemplateDisableUnsubscribeUrlValidationFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.TemplateDisableUnsubscribeUrlValidation,
);

export const selectIsAnonymousUserCriteriaFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.AnonymousUserCriteria,
);

export const selectIsAnonymousUserRetentionFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.AnonymousUserRetentionControls,
);

export const selectIsAllCombinatorSearchQueryFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.DefaultCombinatorsToAll,
);

export const selectIsMultiSelectInputComparatorsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.MultiSelectInputComparators,
);

export const selectIsMultiSelectWithListMembershipFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SegmentationMultipleListsMembership,
);

export const selectIsSegmentAnalysisEmailOpenRateFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.SegmentAnalysisEmailOpenRate,
);

export const selectIsUseRegexInSearchQueryFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.UseRegexInSearchQuery,
);

export const selectIsWhatsAppMessageChannelGAFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.WhatsAppMessageChannelGA,
);

export const selectIsWhatsAppInfobipLocalesEnabled = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.WhatsAppMessageChannelGA,
);

export const selectIsFrequencyManageCappingFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.FrequencyCappingMessageType,
);

export const selectIsFrequencyManageOptimizeFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.FrequencyOptimizeMessageType,
);

export const selectIsFrequencyOptimizationReportingFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.FrequencyOptimizationReporting,
);

export const selectIsEditSingleSavedRowModeFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.EditSingleSavedRowMode,
);

export const selectIsReactImportEmailTemplateFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ReactImportEmailTemplate,
);

export const selectIsReactSpamReportFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ReactSpamReport,
);

export const selectIsReactBEEBuilderTemplatesFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ReactBEEBuilderTemplates,
);

export const selectIsDisplayEmailOnAcidPreviewsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.DisplayEmailOnAcidPreviews,
);

export const selectIsDisplayFinishedCampaignSubfoldersFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.DisplayFinishedCampaignSubfolders,
);

export const selectIsCampaignsHideFoldersFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CampaignsHideFolders,
);

export const selectIsCampaignsFavoritesFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CampaignsFavorites,
);

export const selectIsMissionControlHomepageFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.MissionControlHomepage,
);

export const selectIsMissionControlBetaFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.MissionControlBeta,
);

export const selectIsMovableInkFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.MovableInk,
);

/**
 * Returns true if both `MissionControlHomepage` and `MissionControlBeta` are enabled
 */
export const selectIsMissionControlHomepageAndBetaFeatureFlagOn = createSelector(
  [selectIsMissionControlHomepageFeatureFlagOn, selectIsMissionControlBetaFeatureFlagOn],
  (isMissionControlHomepageEnabled, isMissionControlBetaEnabled) =>
    !!(isMissionControlHomepageEnabled && isMissionControlBetaEnabled),
);

export const selectIsProjectSettingsModernizationFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.ProjectSettingsModernization,
);

export const selectIsHoldUntilReplyTileFFOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.JourneysHoldUntilReplyTile,
);

export const selectIsWhatsAppNewCharLimitsFeatureFlagOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.WhatsAppNewCharLimits,
);

export const selectIsCombinedAddToListFFOn = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.CombinedAddtoList,
);

/**
 * Selects a function that checks if the user has the specified RBAC-style permission
 */
export const selectDoesUserHaveRbacPermission = createSelector(
  [selectFeatureFlags, selectUserRbacPermissions, selectIsUserContextLoaded],
  (featureFlags, userRbacPermissions) => (requestedRbacPermission: number | number[]) =>
    doesRbacPermissionHaveAccess(userRbacPermissions, requestedRbacPermission),
);

/**
 * Selects a function that checks if the user has the permission to manage org members within a given project
 */
export const selectDoesUserHaveRbacPermissionByProject = createSelector(
  [selectFeatureFlags, selectProjects, selectUserRbacPermissions],
  (featureFlags, userProjects, userRbacPermissions) => (projectId: number) =>
    doesRbacPermissionHaveAccess(userRbacPermissions, ORG_PERMISSIONS.ManageMembers) &&
    !!userProjects?.some((project) => project.id === projectId),
);

export const selectDoesUserHaveAccessCheck = createSelector(
  [
    selectFeatureFlags,
    selectRole,
    selectDoesUserHaveRbacPermission,
    selectUserAccountType,
    selectIsUserContextLoaded,
  ],
  (featureFlags, accountRole, rbacPermissionCheck, userAccountType, isUserContextLoaded) =>
    (
      permission: any,
      requiredFeatureFlags: NavbarOptionFeatureFlag[],
      rbacPermission: ProjectPermission | ProjectPermission[],
      hideFromProspect?: boolean,
    ) => {
      let hasPermission = false;

      if (isUserContextLoaded) {
        hasPermission = !rbacPermission || rbacPermissionCheck(rbacPermission);

        // check if prospects are allowed to see the link
        if (hideFromProspect && userAccountType === USER_ACCOUNT_TYPES.Prospect)
          hasPermission = false;
      }

      if (!hasPermission) return false;

      const flagsToCheck = (
        Array.isArray(requiredFeatureFlags) ? requiredFeatureFlags : [requiredFeatureFlags]
      ).filter(Boolean);

      return hasFeatureFlagEnabled(featureFlags, flagsToCheck);
    },
);

export const selectUseFilteredTracking = createSelector(
  [selectFeatureFlags],
  (featureFlags) => !!featureFlags.UseNewInAppTrackingLogic,
);

export const selectOtherProjects = createSelector(
  [selectUserProject, selectProjects],
  (project, projects) => projects?.filter((proj) => proj.id !== project?.id) || [],
);

export const selectWysiwigEditorEnabled = createSelector([selectUserProject], (project) =>
  get(project, 'wysiwygEditorMode'),
);

export const selectShouldShowGdprBanner = createSelector(
  [selectIsBossOrSalesBossLevel, selectIsGdprAccepted],
  (isBaus, isGdprAccepted) => !isGdprAccepted && !isBaus,
);

export const selectUserContextForSegmentTracking = createSelector(
  [userContextSelector],
  (context) => ({
    email: get(context, 'email'),
    username: get(context, 'email'),
    user_id: get(context, 'userId'),
    first_name: get(context, 'firstName'),
    last_name: get(context, 'lastName'),
    project_id: get(context, ['project', 'id']),
    groupId: get(context, 'orgId', '')?.toString() || '',
    accountType: get(context, 'accountType'),
  }),
);

export const selectIsUuidBasedProject = createSelector(
  [selectUserProject],
  (userProject) => !!userProject?.isUuidBased,
);

export const selectUuidProjectType = createSelector(
  [selectUserProject],
  (userProject) => userProject?.uuidProjectType,
);

export const selectUserContextForRum = createSelector([userContextSelector], (context) => ({
  id: new Sha256().update(get(context, 'email', '') || '').digest('hex'),
  orgId: get(context, 'orgId', null),
  companyName: get(context, 'companyName', ''),
  projectId: get(context, ['project', 'id'], 0),
  projectName: get(context, ['project', 'name'], ''),
  accountType: get(context, 'accountType'),
  isUuidBased: get(context, ['project', 'isUuidBased'], false).toString(),
  isBossUser: get(context, 'isBossOrSalesBossLevel', false).toString(),
  isOrgAdmin: get(context, 'orgAdmin', false).toString(),
}));
